// src/utils/parseApiError.js
// Helper central: convierte CUALQUIER error (red, timeout, abort o del backend)
// al mismo formato normalizado para la UI (ver PROPERTY_UPLOAD_ERROR_HANDLING_FRONTEND.md §7).
import axios from 'axios';

/**
 * Normaliza un error de Axios a una estructura única para la UI.
 * @param {*} error Error capturado en el catch de una llamada Axios.
 * @returns {{
 *   type: string,
 *   status: number|null,
 *   requestId: string|null,
 *   userMessage: string,
 *   fieldErrors: Record<string, string>,
 *   errors: Array<{field:string, code:string, message:string}>,
 *   retriable: boolean
 * }}
 */
export function parseApiError(error) {
  // 1) Cancelación intencional (AbortController)
  if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED') {
    return base('NETWORK', 'La operación fue cancelada.', {
      retriable: false,
      code: 'REQUEST_ABORTED',
    });
  }

  // 2) Timeout de Axios
  if (error?.code === 'ECONNABORTED' || /timeout/i.test(error?.message || '')) {
    return base(
      'TIMEOUT',
      'La solicitud tardó demasiado. Reducí el peso de las imágenes e intentá de nuevo.',
      { retriable: true, code: 'REQUEST_TIMEOUT' }
    );
  }

  // 3) Sin respuesta => la request nunca llegó (sin internet / servidor caído / CORS)
  if (!error?.response) {
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    return base(
      'NETWORK',
      offline
        ? 'Sin conexión a internet. Revisá tu red e intentá nuevamente.'
        : 'No pudimos conectar con el servidor. Verificá tu conexión e intentá de nuevo.',
      { retriable: true, code: 'NETWORK_ERROR' }
    );
  }

  // 4) Respuesta del backend con envelope estándar
  const { status, data, headers } = error.response;
  const requestId = data?.requestId || headers?.['x-request-id'] || null;
  const type = data?.type || (status >= 500 ? 'SERVER' : 'VALIDATION');
  const errors = Array.isArray(data?.errors) ? data.errors : [];

  const fieldErrors = {};
  for (const e of errors) {
    if (e.field && e.field !== 'general' && !fieldErrors[e.field]) {
      fieldErrors[e.field] = e.message;
    }
  }

  return {
    type,
    status,
    requestId,
    userMessage: data?.message || defaultMessageFor(type, status),
    fieldErrors,
    errors,
    retriable: type === 'SERVER' || type === 'TIMEOUT' || status === 429 || status >= 500,
  };
}

function base(type, msg, { retriable, code }) {
  return {
    type,
    status: null,
    requestId: null,
    userMessage: msg,
    fieldErrors: {},
    errors: [{ field: 'general', code, message: msg }],
    retriable,
  };
}

function defaultMessageFor(type, status) {
  switch (type) {
    case 'PAYLOAD_TOO_LARGE':
      return 'El envío es demasiado grande. Subí menos archivos por vez.';
    case 'NOT_FOUND':
      return 'El recurso solicitado no existe.';
    case 'SERVER':
      return 'Error del servidor. Intentá más tarde o reportá el problema.';
    default:
      return status >= 500 ? 'Error inesperado del servidor.' : 'Revisá los datos e intentá de nuevo.';
  }
}
